import React, { useContext, useEffect, useRef, useState } from 'react';
import TitleSection from 'components/layouts/TitleSection';
import { Box, Button, Card, Divider, Stack, TextField } from '@mui/material';
import Logo from 'assets/logo.svg?react';
import { AuthContext } from 'AuthProvider';
import { NotificationCenterContext } from 'components/NotificationCenter';

interface ILoginPageProps {}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            callback: (response: { credential?: string }) => void;
            client_id: string;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              shape?: string;
              size?: string;
              text?: string;
              theme?: string;
              type?: string;
              width?: number;
            },
          ) => void;
        };
      };
    };
  }
}

const LoginPage: React.FC<ILoginPageProps> = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const { authGoogleUser, authUser } = useContext(AuthContext);
  const { pushNotification } = useContext(NotificationCenterContext);
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) {
      return;
    }

    const renderGoogleButton = () => {
      if (!window.google || !googleButtonRef.current) {
        return;
      }

      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async ({ credential }) => {
          if (!credential) {
            pushNotification('Google login failed', 'error');
            return;
          }

          try {
            setLoginLoading(true);
            await authGoogleUser(credential);
          } catch (e) {
            console.error(e);
            pushNotification('Google login failed', 'error');
          } finally {
            setLoginLoading(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        size: 'large',
        text: 'continue_with',
        theme: 'outline',
        type: 'standard',
        width: 400,
      });
    };

    if (window.google) {
      renderGoogleButton();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    script.onerror = () => pushNotification('Google script failed to load', 'error');
    document.body.appendChild(script);
  }, [authGoogleUser, googleClientId, pushNotification]);

  const submitForm = async () => {
    try {
      setLoginLoading(true);
      await authUser({ username, password });
    } catch (e) {
      console.error(e);
      pushNotification('Wrong credentials', 'error');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <TitleSection>
      <Stack
        direction={'column'}
        justifyContent={'center'}
        sx={{ width: 800, height: '100vh' }}
      >
        <Card sx={{ py: 5 }}>
          <Box>
            <Box component={Logo} sx={{ color: 'primary.main' }} />
          </Box>
          <form
            onSubmit={(evt) => {
              submitForm();
              evt.preventDefault();
            }}
          >
            <Stack
              direction={'column'}
              gap={3}
              my={5}
              maxWidth={400}
              mx={'auto'}
            >
              <TextField
                fullWidth
                label={'Login'}
                placeholder={'Type your login'}
                name={'my-vocabulary-username'}
                value={username}
                onChange={(evt) => setUsername(evt.target.value)}
              />

              <TextField
                fullWidth
                label={'Password'}
                type={'password'}
                name={'my-vocabulary-password'}
                placeholder={'Type your password'}
                value={password}
                onChange={(evt) => setPassword(evt.target.value)}
              />

              <input type="submit" hidden />

              <Button
                variant={'contained'}
                loading={loginLoading}
                onClick={submitForm}
              >
                {'Login'}
              </Button>

              <Divider>{'or'}</Divider>

              <Box ref={googleButtonRef} sx={{ minHeight: 44 }} />
            </Stack>
          </form>
        </Card>
      </Stack>
    </TitleSection>
  );
};

export default LoginPage;
